import { cn } from "@base/lib/utils";
import { useDebounce } from "@base/model/hooks";
import { FullPageSheet } from "@base/ui/FullPageSheet";
import { Button } from "@buildblockinc/buildblock-design-system";
import type { ProjectTradeItem } from "@features/write-daily-log/model/projectTradesSchema";
import type { ResourceItem } from "@features/write-daily-log/model/schema";
import { useCreateProjectTradeMutation } from "@features/write-daily-log/model/useCreateProjectTradeMutation";
import { useProjectTradesQuery } from "@features/write-daily-log/model/useProjectTradesQuery";
import { SearchBar } from "@features/write-daily-log/ui/SearchBar";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useMemo, useState } from "react";

const SKELETON_WIDTHS = [45, 60, 35, 55, 40];

interface EquipmentSearchSheetProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  currentEquipments: ResourceItem[];
  onPickCatalogItem: (item: ProjectTradeItem) => void;
  onRemove: (id: string) => void;
}

export function EquipmentSearchSheet({
  projectId,
  isOpen,
  onClose,
  currentEquipments,
  onPickCatalogItem,
  onRemove,
}: EquipmentSearchSheetProps) {
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 300);

  const { data, isPending, isError } = useProjectTradesQuery(projectId, debouncedKeyword, "EQUIPMENT", {
    enabled: isOpen && !!projectId,
  });

  const createTrade = useCreateProjectTradeMutation({
    onSuccess: (created) => {
      onPickCatalogItem(created);
      queryClient.invalidateQueries({ queryKey: ["project-trades", projectId] });
    },
    onSettled: () => {
      setKeyword("");
    },
  });

  const handleClose = () => {
    setKeyword("");
    onClose();
  };

  const currentNames = useMemo(() => new Set(currentEquipments.map((e) => e.name)), [currentEquipments]);

  const availableResults = useMemo(() => {
    if (!data?.results) return [];
    return data.results.filter((e) => !currentNames.has(e.name));
  }, [data, currentNames]);

  const trimmedDebounced = debouncedKeyword.trim();

  const resultNamesSet = useMemo(() => new Set(data?.results.map((e) => e.name) ?? []), [data]);

  const isCustomName = useMemo(
    () =>
      trimmedDebounced.length > 0 &&
      !currentNames.has(trimmedDebounced) &&
      !isPending &&
      !resultNamesSet.has(trimmedDebounced),
    [trimmedDebounced, currentNames, isPending, resultNamesSet],
  );

  let emptyMessage: string | null = null;
  if (isError) emptyMessage = "Could not load equipment list.";
  else if (data?.results.length === 0) emptyMessage = "No equipment found.";

  return (
    <FullPageSheet
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel="Select equipment"
      className={cn(
        "md:top-0 md:left-auto md:w-[440px] md:max-w-full md:rounded-none",
        "md:border-l md:border-gray-200 md:shadow-[-4px_0_25px_rgba(0,0,0,0.12)]",
        isOpen ? "md:translate-x-0" : "md:translate-x-full md:translate-y-0",
      )}
    >
      <div className="p-4">
        <SearchBar value={keyword} onChange={setKeyword} onBack={handleClose} disabled={createTrade.isPending} />

        {currentEquipments.length > 0 && (
          <div className="flex flex-col gap-y-2 mt-4">
            <h2 className="text-gray-900 text-title6">Selected equipment</h2>
            <ul className="flex flex-col gap-y-2">
              {currentEquipments.map((equipment) => (
                <li key={equipment.id} className="flex gap-x-2 items-center p-4 bg-blue-10 rounded-8">
                  <span className="flex-1 min-w-0 text-gray-900 truncate text-label2">{equipment.name}</span>
                  <button type="button" onClick={() => onRemove(equipment.id)} aria-label={`Remove ${equipment.name}`}>
                    <X width={20} height={20} className="text-gray-500 cursor-pointer" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {isOpen && !!projectId && isPending && (
          <div className="flex flex-col gap-y-2 mt-4">
            <ul className="flex flex-col gap-y-2">
              {SKELETON_WIDTHS.map((width) => (
                <li key={width} className="flex justify-between items-center p-3">
                  <div className="h-4 rounded-4 skeleton-shimmer" style={{ width: `${width}%` }} />
                  <div className="w-5 h-5 rounded-4 skeleton-shimmer shrink-0" />
                </li>
              ))}
            </ul>
          </div>
        )}

        {isOpen && !!projectId && !isPending && !isCustomName && emptyMessage && (
          <p className="mt-4 text-center text-gray-600 text-label3">{emptyMessage}</p>
        )}

        {isCustomName && (
          <Button
            variant="ghost"
            color="secondary"
            size="large"
            className="!justify-start w-full mt-4"
            disabled={createTrade.isPending}
            onClick={() => createTrade.mutate({ projectId, name: trimmedDebounced, type: "EQUIPMENT" })}
          >
            {createTrade.isPending ? "Adding..." : `Add "${trimmedDebounced}"`}
          </Button>
        )}
        {availableResults.length > 0 && (
          <div className="flex flex-col gap-y-2 mt-4">
            <h2 className="p-3 pb-0 text-gray-600 text-label2">Equipment list</h2>
            <ul className="flex flex-col gap-y-2">
              {availableResults.map((item) => (
                <li key={item.id} className="flex justify-between items-center p-2 pl-3">
                  <span className="text-gray-900 text-label2">{item.name}</span>
                  <button
                    type="button"
                    onClick={() => onPickCatalogItem(item)}
                    aria-label={`Add ${item.name}`}
                    className="p-1"
                  >
                    <Plus width={20} height={20} className="text-gray-900 cursor-pointer" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </FullPageSheet>
  );
}
